import { ArrowDownIcon, ArrowUpIcon, CloseIcon, SearchIcon } from '@chakra-ui/icons';
import {
    Box,
    Button,
    Flex,
    Icon,
    Input,
    InputGroup,
    InputLeftElement,
    InputRightElement,
    Skeleton,
    Spinner,
    Table,
    TableContainer,
    Tag,
    TagCloseButton,
    TagLabel,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useColorModeValue
} from "@chakra-ui/react";
import React, { useEffect, useRef, useState } from 'react';
import { FiChevronLeft, FiChevronRight, FiChevronsLeft, FiChevronsRight } from 'react-icons/fi';
import { useExpanded, useFilters, useGlobalFilter, usePagination, useRowSelect, useSortBy, useTable } from "react-table";
import './appTable.css';

const searchFields = [
    { label: 'Ad Account', value: 'adsAccount' },
    { label: 'Campaign', value: 'campaign' },
    { label: 'Client', value: 'clientName' },
  ];
  

function AppTableForAnalytics({ columns, data = [], searchEnabled, isLoading, isFetching, initialState, paginationMeta = {}, onPageChange, onSearchInputChange, searchTerm, emptyMessage }) {

    const backgroundColor = useColorModeValue('gray.50', 'gray.700');
    const borderColor = useColorModeValue('gray.100', 'gray.700');
    const [showError, setShowError] = useState(false);
    const [currentPage, setCurrentPage] = useState(0);
    const [searchSuggestions, setSearchSuggestions] = useState([]);
    const [activeFilters, setActiveFilters] = useState([]);
    const [isFocused, setIsFocused] = useState(false);

    useEffect(() => {
        onPageChange(currentPage + 1)
    }, [currentPage]);

    const { pageSize: pageSize_, totalItems, totalPages, hasNextPage, hasPrevPage, page:pageNo } = paginationMeta;

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
    } = useTable(
        {
            columns,
            data,
            manualPagination: true,
            initialState: {
                pageIndex: currentPage ?? 0,
                pageSize: pageSize_ ?? 5,
                pageCount: totalPages,
                canPreviousPage: hasPrevPage,
                canNextPage: hasNextPage,
                ...initialState
            },
        },
        useFilters,
        useGlobalFilter,
        useSortBy,
        useExpanded,
        usePagination,
        useRowSelect,
    );


    /* SEARCH */

    const inputRef = useRef();
    const [searchValue, setsearchValue] = useState(searchTerm)
    const [hasSearched, setHasSearched] = useState(false);


    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (searchValue?.length > 2) {
                const suggestions = searchFields.map(field => ({
                    label: `${field.label} contains ${searchValue}`,
                    field: field.value,
                    value: searchValue
                }));
                setSearchSuggestions(suggestions);
                setHasSearched(true);
            }
        }, 400);
    return () => clearTimeout(delayDebounceFn);
    }, [searchValue]);

    // Update parent when filters change
    useEffect(() => {
        const query = {};
      
        activeFilters.forEach(filter => {
          if (!query[filter.field]) {
            query[filter.field] = [];
          }
          query[filter.field].push(filter.value);
        });
      
        onSearchInputChange(query);
      
        if (activeFilters?.length === 0) {
          setSearchSuggestions([]);
        }
    }, [activeFilters]);      

    const handleSuggestionClick = (suggestion) => {
        const alreadyExists = activeFilters.some(
            (f) => f.field === suggestion.field && f.value === suggestion.value
        );

        if (!alreadyExists) {
            setActiveFilters((prev) => [
                ...prev,
                {
                    field: suggestion.field,
                    label: searchFields.find((s) => s.value === suggestion.field).label,
                    value: suggestion.value,
                },
            ]);
        }
      
        setsearchValue(''); // Clear the search input
        setIsFocused(false); // Close the suggestions dropdown
      };
      


    useEffect(() => {

        if (data?.length == 0) {
            setShowError(true);
            setActiveFilters([]);
            //setsearchValue('');
            onSearchInputChange('');
        }

    }, [data])

    useEffect(() => {
        if (showError) {
            setTimeout(() => {
                setShowError(false)
            }, 3000);
        }
    }, [showError])

    // console.log({ hasSearched, data });

    const handleBlur = () => {
        setTimeout(() => {
            setIsFocused(false);
        }, 500); // Delay to allow the click on suggestion to register
    };

    return (

        <Box >
        {
            hasSearched ? (
                <Box >
                    <>
                        {searchEnabled && (
                           <Box position="relative" mb={4}>
                            <InputGroup>
                                <InputLeftElement pointerEvents="none" children={<SearchIcon color="gray.300" />} />
                                <Input
                                ref={inputRef}
                                type="text"
                                value={searchValue || ""}
                                onChange={(e) => {
                                    setsearchValue(e.target.value);
                                }}
                                placeholder="Search..."
                                onFocus={() => setIsFocused(true)}  // Set focus state to true
                                onBlur={handleBlur}  // Set focus state to false
                                />
                                {searchValue && (
                                    <InputRightElement
                                        cursor="pointer"
                                        children={isFetching ? <Spinner /> : <CloseIcon fontSize={14} />}
                                        onClick={() => {
                                        setsearchValue('');
                                        setSearchSuggestions([]);
                                        }}
                                    />
                                )}
                            </InputGroup>

                            {showError ? (
                                    <Flex flexDirection={'column'} gap={'10px'} justifyContent={'center'} alignItems={'center'}>
                                        <Text mt={4} fontSize={'sm'} color='red.500'>No results found. Try a different search term.</Text>
                                    </Flex>
                            ) : null}
                            
                            {isFocused && searchSuggestions?.length > 0 && (
                                <Box
                                    position="absolute"
                                    top="100%"
                                    left={0}
                                    width="50%"
                                    bg="white"
                                    zIndex={10}
                                    border="1px solid"
                                    borderColor="gray.200"
                                    borderRadius="md"
                                    mt="2"
                                    boxShadow="md"
                                >
                                {searchSuggestions.map((suggestion, idx) => (
                                    <Box
                                        key={idx}
                                        px={4}
                                        py={2}
                                        _hover={{ backgroundColor: 'rgb(245,246,247)', cursor: 'pointer' }}
                                        onClick={() => handleSuggestionClick(suggestion)}
                                    >
                                         <Text fontSize="14px">
                                            {(() => {
                                                const parts = suggestion.label.split(/(contains)/gi);
                                                return parts.map((part, i) => {
                                                if (part.toLowerCase() === 'contains') {
                                                    return (
                                                    <Text as="span" key={i} color="rgb(115,115,115)">
                                                        {part}
                                                    </Text>
                                                    );
                                                } else if (i > 0 && parts[i - 1].toLowerCase() === 'contains') {
                                                    // This part is right after "contains"
                                                    return (
                                                    <Text as="span" key={i} fontWeight="bold" color='gray.800'>
                                                        {part}
                                                    </Text>
                                                    );
                                                } else {
                                                    return (
                                                    <Text as="span" key={i} color='gray.800'>
                                                        {part}
                                                    </Text>
                                                    );
                                                }
                                                });
                                            })()}
                                            </Text>
                                  </Box>
                                ))}
                                </Box>
                            )}
                           </Box>                         
                        )}

                        {/* Tags */}
                        {activeFilters?.length > 0 ?
                            <Flex gap={2} my={4}>
                                    {activeFilters.map((filter, idx) => (
                                    <Tag key={idx} size="md" border={'1px solid'} borderRadius={'2xl'} borderColor={'gray.200'} bg={'gray.50'} color={'gray.80'} py={1}>
                                        <TagLabel fontSize={'12px'} color='gray.800'>{filter.label} contains <strong>{filter.value}</strong></TagLabel>
                                        <TagCloseButton onClick={() => {
                                            setActiveFilters(prev => prev.filter((_, i) => i !== idx));
                                        }} />
                                    </Tag>
                                ))}
                            </Flex>
                        :null}

                        <TableContainer >
                            {isLoading && (
                                <Skeleton height='5px' />
                            )}
                            <Table
                                className={'sticky'}
                                variant={'simple'}
                                size='md'
                                {...getTableProps()}
                                borderWidth={1}
                                borderColor={borderColor}

                            >
                                <Thead bg={backgroundColor} >
                                    {headerGroups.map(headerGroup => (
                                        <Tr {...headerGroup.getHeaderGroupProps()}>
                                            {headerGroup.headers.map(column => (
                                                <Th {...column.getHeaderProps(column.getSortByToggleProps())}>
                                                    <Flex align={'center'} gap={'10px'}>
                                                        <Box as='span'> {column.render("Header")} </Box>
                                                        {column.isSorted &&
                                                            (
                                                                <Box as="span">
                                                                    {column.isSortedDesc
                                                                        ? (<ArrowDownIcon boxSize={3} ml={2} />)
                                                                        : (<ArrowUpIcon boxSize={3} ml={2} />)
                                                                    }
                                                                </Box>
                                                            )
                                                        }
                                                        <Box ml={2} as='span'>{column?.canFilter ? column.render('Filter') : null}</Box>
                                                    </Flex>
                                                </Th>
                                            ))}
                                        </Tr>
                                    ))}
                                </Thead>
                                <Tbody {...getTableBodyProps()}>
                                    {page.map((row, i) => {
                                        prepareRow(row);
                                        // //console.log(row);
                                        return (
                                            <React.Fragment key={i + 'row'}>
                                                <Tr  {...row.getRowProps()}>
                                                    {row.cells.map(cell => {
                                                        return (
                                                            <Td {...cell.getCellProps()}
                                                                fontSize={'sm'}
                                                            >
                                                                {cell.render("Cell")}
                                                            </Td>
                                                        );
                                                    })}
                                                </Tr>
                                            </React.Fragment>
                                        );
                                    })}
                                </Tbody>
                            </Table>


                        </TableContainer>
                        {totalPages > 1 && (
                            <Flex align={'center'} justify={['start', 'end']} mt={'40px'} gap={'5px'}>
                                <Box mr={2} fontSize='sm'>
                                    Page{' '}
                                    <Text fontWeight={'bold'} display={'inline-flex'}>
                                        {currentPage + 1} of {totalPages}
                                    </Text>{' '}
                                </Box>

                                <Button size={'xs'} onClick={() => {
                                    setCurrentPage(0);
                                }} isDisabled={!hasPrevPage}>
                                    <Icon boxSize={4} as={FiChevronsLeft} />
                                </Button>{' '}

                                <Button size={'xs'} onClick={() => {
                                    if (currentPage + 1 > 1) {
                                        setCurrentPage(currentPage - 1);
                                    }
                                }} isDisabled={!hasPrevPage} >
                                    <Icon boxSize={4} as={FiChevronLeft} />
                                </Button>{' '}

                                <Button size={'xs'} onClick={() => {
                                    if (currentPage + 1 < totalPages) {
                                        setCurrentPage(currentPage + 1);
                                    }
                                }} isDisabled={!hasNextPage}>
                                    <Icon boxSize={4} as={FiChevronRight} />
                                </Button>{' '}

                                <Button size={'xs'} onClick={() => {
                                    setCurrentPage(totalPages - 1)
                                }} isDisabled={!hasNextPage}>
                                    <Icon boxSize={4} as={FiChevronsRight} />
                                </Button>{' '}

                            </Flex>
                        )}
                    </>
                </Box>
            ) : (
                <>
                    {data?.length == 0 ? (
                        <>
                            {emptyMessage ? emptyMessage : 'No data available'}
                        </>
                    ) : (
                        <>
                            {searchEnabled && (
                                <Box mb={'20px'}>
                                    <InputGroup>
                                        <InputLeftElement
                                            pointerEvents='none'
                                            children={<SearchIcon color='gray.300' />}
                                        />
                                        <Input
                                            ref={inputRef}
                                            type="text"
                                            value={searchValue || ""}
                                            onChange={e => {
                                                setsearchValue(inputRef.current.value);
                                            }}
                                            placeholder={`Search...`}
                                        />
                                        {searchValue &&
                                            <InputRightElement
                                                cursor={'pointer'}
                                                children={isFetching ? <Spinner /> : <CloseIcon fontSize={14} _hover={{ color: 'gray.600' }} color='gray.300' />}
                                                onClick={() => { setsearchValue(''); }} />}
                                    </InputGroup>

                                </Box>
                            )}

                            <TableContainer >
                                {isLoading && (
                                    <Skeleton height='5px' />
                                )}
                                <Table
                                    className={'sticky'}
                                    variant={'simple'}
                                    size='md'
                                    {...getTableProps()}
                                    borderWidth={1}
                                    borderColor={borderColor}

                                >
                                    <Thead bg={backgroundColor} >
                                        {headerGroups.map(headerGroup => (
                                            <Tr {...headerGroup.getHeaderGroupProps()}>
                                                {headerGroup.headers.map(column => (
                                                    <Th {...column.getHeaderProps(column.getSortByToggleProps())}>
                                                        <Flex align={'center'} gap={'10px'}>
                                                            <Box as='span'> {column.render("Header")} </Box>
                                                            {column.isSorted &&
                                                                (
                                                                    <Box as="span">
                                                                        {column.isSortedDesc
                                                                            ? (<ArrowDownIcon boxSize={3} ml={2} />)
                                                                            : (<ArrowUpIcon boxSize={3} ml={2} />)
                                                                        }
                                                                    </Box>
                                                                )
                                                            }
                                                            <Box ml={2} as='span'>{column?.canFilter ? column.render('Filter') : null}</Box>
                                                        </Flex>
                                                    </Th>
                                                ))}
                                            </Tr>
                                        ))}
                                    </Thead>
                                    <Tbody {...getTableBodyProps()}>
                                        {page.map((row, i) => {
                                            prepareRow(row);
                                            // //console.log(row);
                                            return (
                                                <React.Fragment key={i + 'row'}>
                                                    <Tr  {...row.getRowProps()}>
                                                        {row.cells.map(cell => {
                                                            return (
                                                                <Td {...cell.getCellProps()}
                                                                    fontSize={'sm'}
                                                                >
                                                                    {cell.render("Cell")}
                                                                </Td>
                                                            );
                                                        })}
                                                    </Tr>
                                                </React.Fragment>
                                            );
                                        })}
                                    </Tbody>
                                </Table>


                            </TableContainer>
                            {/* <Box mt={4}>Showing the first 20 results of {rows.length} rows</Box> */}
                            {totalPages > 1 && (
                                <Flex align={'center'} justify={['start', 'end']} mt={'40px'} gap={'5px'}>
                                    <Box mr={2} fontSize='sm'>
                                        Page{' '}
                                        <Text fontWeight={'bold'} display={'inline-flex'}>
                                            {currentPage + 1} of {totalPages}
                                        </Text>{' '}
                                    </Box>

                                    <Button size={'xs'} onClick={() => {
                                        setCurrentPage(0);
                                    }} isDisabled={!hasPrevPage}>
                                        <Icon boxSize={4} as={FiChevronsLeft} />
                                    </Button>{' '}

                                    <Button size={'xs'} onClick={() => {
                                        if (currentPage + 1 > 1) {
                                            setCurrentPage(currentPage - 1);
                                        }
                                    }} isDisabled={!hasPrevPage} >
                                        <Icon boxSize={4} as={FiChevronLeft} />
                                    </Button>{' '}

                                    <Button size={'xs'} onClick={() => {
                                        if (currentPage + 1 < totalPages) {
                                            setCurrentPage(currentPage + 1);
                                        }
                                    }} isDisabled={!hasNextPage}>
                                        <Icon boxSize={4} as={FiChevronRight} />
                                    </Button>{' '}

                                    <Button size={'xs'} onClick={() => {
                                        setCurrentPage(totalPages - 1)
                                    }} isDisabled={!hasNextPage}>
                                        <Icon boxSize={4} as={FiChevronsRight} />
                                    </Button>{' '}

                                </Flex>
                            )}
                        </>
                    )}
                </>
            )
        }
        </Box>
    )
}

export default AppTableForAnalytics;